/**
 * Connect your AI's routes (slice 1b spec §4.6). Each answers at once; the
 * `computer` hub event and `/api/me` follow the connection to `connected` or
 * `failed`, so every mutation refetches `/api/me`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../app/api";
import { meKey, useMe, type AiProvider, type Me } from "../../app/me";
import { usePeople } from "../people/api";
import { askNotice, canAsk, connectHref, type Catalog } from "./onboarding";

/** The model catalog (`GET /api/ai/models`): fixed per deploy. */
export function useModels() {
  return useQuery({
    queryKey: ["ai-models"],
    queryFn: async () => (await api<{ providers: Catalog }>("GET", "/api/ai/models")).providers,
    staleTime: Infinity,
  });
}

type Ai = NonNullable<Me["computer"]>["ai"];

/**
 * A mutation that moves the connection: its `{ ai }` goes into the cached
 * `/api/me` at once (so "Connecting…" shows before the refetch), then `/api/me`
 * is refetched either way.
 */
function useAiMutation<T>(run: (input: T) => Promise<{ ai?: Ai }>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: ({ ai }) => {
      if (!ai) return;
      client.setQueryData<Me | null>(meKey, (me) =>
        me?.computer ? { ...me, computer: { ...me.computer, ai } } : me,
      );
    },
    onSettled: () => client.invalidateQueries({ queryKey: meKey }),
  });
}

type ConnectInput = {
  provider: AiProvider;
  model?: string;
  api_key?: string;
  /** The account a Reconnect replaces the credential of. */
  connection_id?: string;
  /** Workers AI's Cloudflare account ID (slice 1h spec §3.2). */
  account_id?: string;
};

/** `POST /api/computer/ai`: a key is checked with the provider first (`invalid_key`). */
export function useConnectAi() {
  return useAiMutation((input: ConnectInput) => api<{ ai: Ai }>("POST", "/api/computer/ai", input));
}

type Routing = {
  model: string;
  fallbacks: string[];
  /** Slice 2f: the image model; null for none, absent to leave it. */
  image?: { model: string; fallbacks: string[] } | null;
};

/**
 * `PUT /api/computer/ai/routing` (slice 1c spec §7): its `202` still has the
 * old routing while `ConnectAI` writes it, so the cache shows the new one
 * at once; a routing that doesn't apply comes back with the old one and why.
 */
export function useRouting() {
  return useAiMutation(async (routing: Routing) => {
    const { ai } = await api<{ ai: Ai }>("PUT", "/api/computer/ai/routing", routing);
    return { ai: { ...ai, ...routing } };
  });
}

/** `DELETE /api/computer/ai/connections/:id`: `409 in_use` for the default's last account. */
export function useDisconnect() {
  return useAiMutation((id: string) =>
    api<{ ai: Ai }>("DELETE", `/api/computer/ai/connections/${id}`),
  );
}

/** `POST /api/computer/ai/connections/:id/move`: one place up or down in its provider's order. */
export function useMoveAccount() {
  return useAiMutation(({ id, direction }: { id: string; direction: "up" | "down" }) =>
    api<{ ai: Ai }>("POST", `/api/computer/ai/connections/${id}/move`, { direction }),
  );
}

/**
 * `POST /api/computer/ai/health` (slice 1c spec §5.1): reads health and
 * usage if the computer is awake. An asleep computer (`409 asleep`) keeps
 * the stored read, and a Worker without the route (`404`) has none: both
 * are nothing to show.
 */
export function useReadHealth() {
  return useAiMutation(() =>
    api<{ ai: Ai }>("POST", "/api/computer/ai/health").catch(nothingToRead),
  );
}

const QUIET = new Set([404, 409]);

/** `404` (no route yet) and `409 asleep` leave the stored read; anything else is an error. */
function nothingToRead(error: unknown): { ai?: Ai } {
  if (error instanceof ApiError && QUIET.has(error.status)) return {};
  throw error;
}

/** `DELETE /api/computer/ai/pending`: cancels a sign-in (ChatGPT's or Grok's). */
export function useCancelSignIn() {
  return useAiMutation(() => api<{ ai?: Ai }>("DELETE", "/api/computer/ai/pending"));
}

/**
 * Whether asks go through, and where to connect when they don't. A member
 * can't connect the AI: `owner` names who can (slice 6a §2), instead of a link.
 */
export function useAskGate(): AskGate {
  const { data: me } = useMe();
  const computer = me?.computer ?? null;
  const open = canAsk(computer);
  const notice = askNotice(computer);
  const member = me?.user.role === "member";
  const people = usePeople({ enabled: member && !open });
  if (!member) return { open, href: connectHref(computer), notice };
  const owner = people.data?.people.find((p) => p.role === "owner")?.name ?? "The owner";
  return { open, href: null, owner, notice };
}

/** `notice`: the composer's strip (slice 1j §6.1), which then says what the line under it would. */
export type AskGate = {
  open: boolean;
  href: string | null;
  owner?: string;
  notice?: "updating" | "locked" | null;
};
